import classNames from 'classnames'
import EmptyHolder from 'components/EmptyHolder'
import _ from 'lodash'
import {renderDefaultError} from 'modules/asyncCache/components/LazyPagination'
import useTranslate from 'modules/local/useTranslate'
import React, {useMemo} from 'react'
import {renderLoading} from 'views/MainPage/functions/renderPagingLoading'

const TableWrapper = ({ children, headers = [], schemas = [] }) => {
  const t = useTranslate()

  const hasColWidths = schemas.some(s => s.colWidth)

  const content = useMemo(
    () =>
      Array.from(headers).map((title, index) => (
        <th
          key={index}
          style={{ textAlign: 'center' }}
          className={classNames(
            'px-2 py-1 border text-sm font-medium text-color-300 uppercase',
            schemas[index]?.thClassName
          )}>
          {t(title).replace('?', '')}
        </th>
      )),
    [headers, schemas]
  )

  return (
    <table className={classNames('w-full mb-3', { 'table-fixed': hasColWidths })}>
      {hasColWidths && (
        <colgroup>
          {schemas.map((schema, index) => (
            <col key={index} style={schema.colWidth ? { width: schema.colWidth } : undefined} />
          ))}
        </colgroup>
      )}
      <thead>
        <tr>{content}</tr>
      </thead>
      <tbody>
        {children}
      </tbody>
    </table>
  )
}

export const renderEmptyRow =
  ({ cols = 1, Component = EmptyHolder } = {}) =>
  () =>
    (
      <tr>
        <td
          className="border p-2"
          colSpan={_.defaultTo(cols, 0)}>
          <Component />
        </td>
      </tr>
    )

export const renderReportTable = (headers, schemas = []) => (child, __, total) => (
    <TableWrapper
      total={total}
      headers={headers}
      schemas={schemas}>
      {child}
    </TableWrapper>
)

export const renderReportTableLoading = (headers, loader = renderLoading()) => {
  const cols = Array.from(headers ?? []).length
  return () => (
    <tr>
      <td
        colSpan={cols}
        className="border p-2">
        {loader}
      </td>
    </tr>
  )
}

export const withReportTable = (headers, schemas = []) => {
  const cols = Array.from(headers ?? []).length
  return {
    renderError: renderDefaultError,
    renderWrap: renderReportTable(headers, schemas),
    renderLoading: renderReportTableLoading(headers),
    renderEmpty: renderEmptyRow({ cols, Component: EmptyHolder}),
    renderItem: renderReportRow({
      fields: schemas,
    }),
  }
}

export const renderReportRow =
  ({ fields = [], style, className }) =>
  (item, index) =>
    (
      <tr
        key={index}
        className={className}
        style={{
          textAlign: 'center',
          ...(style ?? {}),
        }}>
        {Array.from(fields ?? []).map(
          (
            {
              name,
              style,
              value_path,
              className,
              value2_path,
              className2,
              tdClassName,
              wrapperClassName,
              render = (value, data) => value,
              render2 = (value, data) => value,
            },
            index
          ) => {

            const render1Value = render(_.get(item, value_path), item)
            const render2Value = render2(_.get(item, value2_path), item)

            return (
              <td
                key={index}
                className={classNames('px-2 py-1 border text-sm', tdClassName)}
                style={{
                  textAlign: index === 0 ? 'left' : 'inherit',
                }}>
                <div
                  style={style}
                  className={classNames(
                    'flex flex-col gap-0.5 leading-tight',
                    wrapperClassName
                  )}>
                  <span className={classNames('text-sm', className)}>
                    {isNaN(render1Value)
                      ? render1Value
                      : Number(render1Value).toLocaleString()}
                  </span>
                  {!!_.get(item, value2_path) && !!render2Value && (
                    <span className={classNames('text-color-400 text-xs italic', className2)}>
                        {isNaN(render2Value) ? render2Value : Number(render2Value).toLocaleString()}
                    </span>
                  )}
                </div>
              </td>
            )
          }
        )}
      </tr>
    )
